import { useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { ApiError } from '../shared/api/HttpClient';
import { JanusLiveMediaAdapter } from '../shared/media/JanusLiveMediaAdapter';
import type { LiveMediaState } from '../shared/media/LiveMediaAdapter';
import { connectionQualityLabel, localizedProblem } from '../shared/i18n';
import { GuestApi, type GuestInvitationPreview, type GuestSession } from './GuestApi';
import { browserReady, guestStatus, invitationToken } from './guestModel';
import './guest.css';

const EMPTY_MEDIA: LiveMediaState = { connection: 'closed', quality: 'POOR', microphoneLevel: 0, latencyMs: null, jitterMs: null, packetLossPercent: null, monitorStream: null };

type Step = 'LOADING' | 'PREFLIGHT' | 'READY' | 'CONNECTING' | 'ROOM' | 'UNAVAILABLE';

export function GuestStudio(): ReactElement {
  const api = useMemo(() => new GuestApi(), []);
  const media = useMemo(() => new JanusLiveMediaAdapter(), []);
  const [token] = useState(() => invitationToken(window.location.hash));
  const [preview, setPreview] = useState<GuestInvitationPreview | null>(null);
  const [session, setSession] = useState<GuestSession | null>(null);
  const [step, setStep] = useState<Step>('LOADING');
  const [microphoneReady, setMicrophoneReady] = useState(false);
  const [headphones, setHeadphones] = useState(false);
  const [testLevel, setTestLevel] = useState(0);
  const [mediaState, setMediaState] = useState<LiveMediaState>(EMPTY_MEDIA);
  const [error, setError] = useState<string | null>(null);
  const testStream = useRef<MediaStream | null>(null);
  const testContext = useRef<AudioContext | null>(null);
  const testFrame = useRef(0);
  const mediaRef = useRef(mediaState);

  useEffect(() => { mediaRef.current = mediaState; }, [mediaState]);
  useEffect(() => media.subscribe(setMediaState), [media]);
  useEffect(() => {
    history.replaceState(null, '', '/guest');
    if (token === null) { setStep('UNAVAILABLE'); return; }
    void api.preview(token).then((value) => { setPreview(value); setStep('PREFLIGHT'); }).catch((reason: unknown) => { setError(problem(reason)); setStep('UNAVAILABLE'); });
  }, [api, token]);
  useEffect(() => () => { stopTest(); void media.disconnect(); }, [media]);

  useEffect(() => {
    if (session === null || step !== 'ROOM') return;
    let active = true;
    const beat = async (): Promise<void> => {
      try {
        const result = await api.heartbeat(session.accessToken, mediaRef.current);
        if (active) setSession((current) => current === null ? null : { ...current, state: result.state });
      } catch (reason: unknown) {
        if (!active) return;
        setError(problem(reason));
        setSession((current) => current === null ? null : { ...current, state: 'DISCONNECTED' });
        await media.setMicrophoneEnabled(false).catch(() => undefined);
      }
    };
    void beat();
    const timer = window.setInterval(() => { void beat(); }, 3_000);
    return () => { active = false; window.clearInterval(timer); };
  }, [api, media, session?.accessToken, step]);

  async function testMicrophone(): Promise<void> {
    setError(null);
    if (!browserReady(globalThis)) { setError('Цей браузер не підтримує захищене WebRTC-аудіо. Відкрийте посилання в актуальному Chrome, Edge, Firefox або Safari.'); return; }
    try {
      stopTest();
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      const context = new AudioContext();
      const analyser = context.createAnalyser();
      analyser.fftSize = 256;
      context.createMediaStreamSource(stream).connect(analyser);
      testStream.current = stream; testContext.current = context; setMicrophoneReady(true);
      const values = new Uint8Array(analyser.fftSize);
      const sample = (): void => {
        analyser.getByteTimeDomainData(values);
        const power = values.reduce((sum, value) => sum + ((value - 128) / 128) ** 2, 0) / values.length;
        setTestLevel(Math.min(1, Math.sqrt(power) * 4));
        testFrame.current = requestAnimationFrame(sample);
      };
      sample();
    } catch (reason: unknown) { setError(problem(reason)); }
  }

  function stopTest(): void {
    cancelAnimationFrame(testFrame.current);
    testStream.current?.getTracks().forEach((track) => track.stop()); testStream.current = null;
    void testContext.current?.close().catch(() => undefined); testContext.current = null; setTestLevel(0);
  }

  function preflightComplete(): void { stopTest(); setStep('READY'); }

  async function join(): Promise<void> {
    if (token === null) return;
    setStep('CONNECTING'); setError(null);
    try {
      const connected = await api.redeem(token);
      setSession(connected);
      await media.connect(connected.credential);
      await media.setMicrophoneEnabled(true);
      setSession({ ...connected, state: 'BACKSTAGE' }); setStep('ROOM');
    } catch (reason: unknown) { setError(problem(reason)); setStep('UNAVAILABLE'); await media.disconnect(); }
  }

  async function leave(): Promise<void> {
    await media.setMicrophoneEnabled(false).catch(() => undefined);
    await media.disconnect();
    setSession((current) => current === null ? null : { ...current, state: 'DISCONNECTED' });
  }

  if (step === 'LOADING') return <main className="guest-studio guest-center"><div className="guest-loader" /><p>Перевіряємо запрошення…</p></main>;
  if (step === 'UNAVAILABLE') return <main className="guest-studio guest-center"><p className="guest-kicker">ГОСТЬОВА СТУДІЯ</p><h1>Посилання недоступне</h1><p className="guest-muted">Запрошення вже використане, прострочене або ефірна кімната закрита. Попросіть ведучого створити нове.</p>{error !== null && <p className="guest-error" role="alert">{error}</p>}</main>;

  if (step === 'ROOM' && session !== null) {
    const status = guestStatus(session.state);
    return <main className={`guest-studio guest-room ${status.onAir ? 'guest-onair' : ''}`}>
      <header><div><p className="guest-kicker">{session.stationName}</p><strong>{session.programName ?? 'Прямий ефір'}</strong></div><span className="guest-secure">● ЗАХИЩЕНО</span></header>
      <section className="guest-room-state" aria-live="assertive"><p>{session.guestName}</p><h1>{status.label}</h1><span>{status.detail}</span></section>
      <section className="guest-live-meter" aria-label="Рівень мікрофона"><i style={{ '--guest-level': `${Math.round(mediaState.microphoneLevel * 100)}%` } as React.CSSProperties} /><div><span>МІКРОФОН</span><strong>{connectionLabel(mediaState.connection)} · {connectionQualityLabel(mediaState.quality)}</strong></div></section>
      <aside><strong>Навушники мають залишатися увімкненими</strong><span>Маршрут до слухачів контролює лише ведучий. Ви не можете випадково вивести себе в ефір.</span></aside>
      {error !== null && <p className="guest-error" role="alert">{error}</p>}
      <footer><button type="button" onClick={() => void leave()} disabled={session.state === 'DISCONNECTED'}>ЗАВЕРШИТИ УЧАСТЬ</button><small>Затримка {mediaState.latencyMs === null ? '—' : `${Math.round(mediaState.latencyMs)} мс`} · втрата {mediaState.packetLossPercent === null ? '—' : `${mediaState.packetLossPercent.toFixed(1)}%`}</small></footer>
    </main>;
  }

  return <main className="guest-studio guest-preflight">
    <header><div className="guest-brand">SL <span>ГІСТЬ</span></div><span className="guest-secure">● ЗАХИЩЕНЕ ЗАПРОШЕННЯ</span></header>
    <section className="guest-intro"><p className="guest-kicker">{preview?.stationName}</p><h1>Вітаємо, {preview?.guestName}</h1><p>{preview?.programName ?? 'Гостьове підключення до прямого ефіру'}</p></section>
    {step === 'PREFLIGHT' ? <section className="guest-checks">
      <div className="guest-check complete"><span>01</span><div><strong>Браузер і з’єднання</strong><small>Запрошення перевірене, захищений канал доступний.</small></div><i>✓</i></div>
      <div className={`guest-check ${microphoneReady ? 'complete' : ''}`}><span>02</span><div><strong>Мікрофон</strong><small>Дозвольте доступ і скажіть кілька слів.</small><div className="guest-test-meter"><b style={{ width: `${Math.round(testLevel * 100)}%` }} /></div></div><button type="button" onClick={() => void testMicrophone()}>{microphoneReady ? 'ПОВТОРИТИ' : 'ПЕРЕВІРИТИ'}</button></div>
      <label className={`guest-check guest-headphones ${headphones ? 'complete' : ''}`}><span>03</span><div><strong>Навушники</strong><small>Це прибирає луну та захищає якість ефіру.</small></div><input type="checkbox" checked={headphones} onChange={(event) => setHeadphones(event.target.checked)} /><i>{headphones ? '✓' : ''}</i></label>
      {error !== null && <p className="guest-error" role="alert">{error}</p>}
      <button className="guest-primary" type="button" disabled={!microphoneReady || !headphones} onClick={preflightComplete}>ПРОДОВЖИТИ</button>
    </section> : <section className="guest-ready"><div className="guest-ready-mark">✓</div><h2>Усе готово</h2><p>Спочатку ви потрапите за лаштунки. Слухачі почують вас лише після команди ведучого.</p><button className="guest-primary" type="button" disabled={step === 'CONNECTING'} onClick={() => void join()}>{step === 'CONNECTING' ? 'ПІДКЛЮЧАЄМО…' : 'УВІЙТИ ЗА ЛАШТУНКИ'}</button></section>}
    <footer className="guest-legal">Одноразове запрошення · аудіо не записується платформою</footer>
  </main>;
}

function connectionLabel(state: RTCPeerConnectionState): string {
  const labels: Record<RTCPeerConnectionState, string> = {
    closed: 'відключено', connected: 'підключено', connecting: 'підключення',
    disconnected: 'зв’язок втрачено', failed: 'помилка', new: 'очікування',
  };
  return labels[state];
}

function problem(reason: unknown): string {
  if (reason instanceof ApiError) return localizedProblem(reason.problem);
  if (reason instanceof DOMException && reason.name === 'NotAllowedError') return 'Дозвольте доступ до мікрофона у налаштуваннях браузера.';
  return reason instanceof Error ? reason.message : 'Не вдалося завершити підключення.';
}
